<template>
  <div class="eg-pv-table">
    <DataTable
      :value="items"
      :loading="loading"
      :row-class="rowClass"
      responsive-layout="scroll"
      @sort="handleSort"
      @row-click="handleRowClick"
    >
      <Column
        v-for="field in fields"
        :key="field.key"
        :field="field.key"
        :sortable="field.sortable || false"
        :header="field.label"
        :header-style="field.style"
        :body-style="field.style"
        :class-name="alignClass(field.align)"
      >
        <template #body="{ data }">
          <slot
            :name="`cell(${field.key})`"
            :item="data"
            :value="data[field.key]"
          >
            <span class="eg-pv-table__cell-default">{{ data[field.key] }}</span>
          </slot>
        </template>
      </Column>

      <template #empty>
        <slot name="empty">
          <div class="eg-pv-table__empty">
            {{ emptyText }}
          </div>
        </slot>
      </template>
    </DataTable>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";

export default {
  name: "EgPvTable",

  components: {
    DataTable,
    Column,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    // [{ key: string, label: string, sortable?: boolean, style?: string, align?: 'left'|'center'|'right' }]
    fields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "No data available",
    },
    rowClass: {
      type: Function,
      default: null,
    },
  },

  methods: {
    alignClass(align) {
      return align ? `eg-pv-table__align-${align}` : null;
    },

    handleSort(event) {
      this.$emit("sort", {
        field: event.sortField,
        order: event.sortOrder === -1 ? "desc" : "asc",
      });
    },

    handleRowClick(event) {
      this.$emit("row-click", event.data);
    },
  },
};
</script>

<style lang="scss">
.eg-pv-table {
  width: 100%;

  .p-datatable {
    width: 100%;
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-radius: 8px;
    overflow: hidden;
  }

  .eg-pv-table__align-center {
    text-align: center;

    .p-column-header-content {
      justify-content: center;
    }
  }

  .eg-pv-table__align-right {
    text-align: right;

    .p-column-header-content {
      justify-content: flex-end;
    }
  }

  .p-datatable-thead > tr > th {
    background: var(--color-surface-neutral-container-1, #f6f8fa);
    color: var(--color-text-neutral-secondary, #566370);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    line-height: 120%;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-right: none;
    white-space: normal;
    user-select: none;

    &:last-child {
      border-right: none;
    }
  }

  .p-sortable-column {
    cursor: pointer;

    &:hover {
      background: var(--color-surface-neutral-container-1, #f0f3f6);
      color: var(--color-text-neutral-on-white, #1a2330);
    }

    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  .p-highlight {
    color: var(--color-text-neutral-on-white, #1a2330) !important;
    background: var(--color-surface-neutral-container-1, #f6f8fa) !important;
  }

  // Sort icon — replace PrimeIcons (not installed) with a CSS arrow
  .p-sortable-column-icon {
    display: inline-block;
    margin-left: 4px;
    width: 12px;
    height: 12px;
    vertical-align: middle;
    opacity: 0.4;
    font-style: normal;

    font-size: 0; // hides the PI font character; pseudo-element renders the arrow instead
    color: transparent;

    &::after {
      content: "↕";
      font-size: 12px;
      color: var(--color-text-neutral-secondary, #566370);
      line-height: 1;
    }
  }

  .p-highlight .p-sortable-column-icon::after {
    content: "↑";
    opacity: 1;
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  // PrimeVue 2 sets aria-sort on the th; use CSS attr trick for desc
  .p-highlight[aria-sort="descending"] .p-sortable-column-icon::after {
    content: "↓";
  }

  .p-datatable-tbody > tr {
    background: var(--color-surface-neutral-background, #fff);
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--color-surface-neutral-container-1, #f6f8fa);
    }

    > td {
      padding: 12px;
      border-right: none;
      vertical-align: middle;
      color: var(--color-text-neutral-on-white, #1a2330);
      font-family: var(--typography-font-family-text-primary, Inter);
      font-size: var(--typography-font-size-fs-300, 14px);
      font-weight: var(--typography-font-weight-fw-400, 400);
      line-height: 150%;
    }
  }

  .p-datatable-emptymessage > td {
    text-align: center;
    padding: 48px 16px;
  }

  &__empty {
    color: var(--color-text-neutral-secondary, #566370);
    font-size: var(--typography-font-size-fs-300, 14px);
  }

  .p-datatable-loading-overlay {
    background: rgba(255, 255, 255, 0.7);
  }

  &__cell-default {
    display: block;
  }
}
</style>
